import { useEffect, useState } from 'react';
import styles from './new-password.module.css';
import eyeIcon from '../../assets/eye-show-svgrepo-com.svg';
import eyeSlashIcon from '../../assets/eye-off-svgrepo-com.svg';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, useNotification } from '../../components';
import { AuthService } from '../../services';
import { NavigationPaths, TOKEN_KEY_NEW_PASS } from '../../constants';

export const NewPassword = () => {
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<boolean>(false);

  useEffect(() => {
    const isOnNewPasswordStep = JSON.parse(localStorage.getItem('setNewPassword') as string);
    if (!isOnNewPasswordStep) {
      addNotification({
        title: t('error'),
        message: t('unauthorizedAccess'),
        type: 'error',
      });
      navigate(NavigationPaths.LOGIN);
    }
  }, []);

  const validatePassword = () => {
    const criteria = [
      { check: password.length >= 8, message: t('minimumCharacters') },
      { check: /[A-Z]/.test(password), message: t('uppercaseLetter') },
      { check: /[a-z]/.test(password), message: t('lowercaseLetter') },
      { check: /[0-9]/.test(password), message: t('leastNumber') },
      {
        check: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        message: t('specialCharacter'),
      },
    ];

    const errorMessages = criteria
      .filter((criterion) => !criterion.check)
      .map((criterion) => criterion.message);

    if (errorMessages.length) {
      addNotification({
        title: t('error'),
        message: [t('passwordCriteria'), ...errorMessages],
        type: 'error',
      });
      setErrors(true);
    }

    if (password !== confirmPassword) {
      addNotification({
        title: t('error'),
        message: t('passwordMatch'),
        type: 'error',
      });
      setErrors(true);
    }

    return password === confirmPassword && errorMessages.length === 0;
  };

  const handleSubmit = async () => {
    if (validatePassword()) {
      const res = await AuthService.resetPassword(password);
      if (res.data) {
        localStorage.removeItem('setNewPassword');
        localStorage.removeItem(TOKEN_KEY_NEW_PASS);
        addNotification({
          title: t('success'),
          message: t('passwordReset'),
          type: 'success',
        });
        navigate(NavigationPaths.LOGIN);
      }
    }
  };

  return (
    <div className={styles['new-password-wrapper']}>
      <div className={styles['new-password-container']}>
        <h1 className={styles['title']}>{t('setNewPassword')}</h1>

        <div className={styles['password-container']}>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder={t('newPassword')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={`${styles['input']} ${errors && styles['error']}`}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className={styles['toggle-password']}
          >
            <img
              src={showPassword ? eyeSlashIcon : eyeIcon}
              alt={showPassword ? 'Hide password' : 'Show password'}
              className={styles['toggle-icon']}
            />
          </button>
        </div>

        <div className={styles['password-container']}>
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder={t('confirmNewPassword')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={`${styles['input']} ${errors && styles['error']}`}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className={styles['toggle-password']}
          >
            <img
              src={showConfirmPassword ? eyeSlashIcon : eyeIcon}
              alt={showConfirmPassword ? 'Hide password' : 'Show password'}
              className={styles['toggle-icon']}
            />
          </button>
        </div>

        <Button
          variant="primary"
          showArrow
          width="100%"
          onClick={handleSubmit}
        >
          {t('confirm')}
        </Button>
      </div>
    </div>
  );
};
